import { Box, Grid, styled } from '@mui/material';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import { InfoTooltip, ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IGeoMappingFormValues } from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import { Field, useFormikContext } from 'formik';
import React, { FC } from 'react';
import MappingItem from '../MappingItem';

const MappingContainer = styled(Box)(() => ({
  background: '#fff',
  borderRadius: 4,
  border: `1px solid #EEEEEE`,
}));

const TableHeaderNames = styled(ThemeTypography)(() => ({
  fontWeight: `300 !important`,
  textTransform: 'uppercase',
}));

const DatasetMappingForm: FC = () => {
  const { values: datasetValues, errors, setValues } = useFormikContext<IGeoMappingFormValues>();

  const notRemovedColumns = datasetValues?.mappedColumns?.filter(({ removed }) => !removed);

  const handleClickExcludeAll = () => {
    setValues({
      ...datasetValues,
      datasetId: datasetValues?.datasetId || '',
      mappedColumns:
        datasetValues?.mappedColumns?.map(column => ({ ...column, removed: true })) || [],
    });
  };

  return (
    <MappingContainer mt={2} px={2} py={2.5}>
      {errors?.mappedColumns?.length && (
        <Box mb={3}>
          <ThemeTypography variant="BODY_LARGE" color="RED">
            {errors.mappedColumns?.[0].toString()}
          </ThemeTypography>
        </Box>
      )}

      <Box mb={1}>
        <Grid container>
          <Grid item xs={3}>
            <FlexBox>
              <TableHeaderNames variant="BODY_LARGE">Uploaded Columns</TableHeaderNames>
              <InfoTooltip size={EAvailableSizes.SMALL} text="The data columns that you uploaded" />
            </FlexBox>
          </Grid>
          <Grid item xs={3}>
            <FlexBox>
              <TableHeaderNames variant="BODY_LARGE">File Sample Data</TableHeaderNames>
              <InfoTooltip
                size={EAvailableSizes.SMALL}
                text="Samples of what is contained in each column of your uploaded dataset"
              />
            </FlexBox>
          </Grid>
          <Grid item xs={3}>
            <FlexBox>
              <TableHeaderNames variant="BODY_LARGE">Template Columns</TableHeaderNames>
              <InfoTooltip
                size={EAvailableSizes.SMALL}
                text="The equivalent column in the system with required formats"
              />
            </FlexBox>
          </Grid>
          <Grid item xs={3}>
            {!!notRemovedColumns?.length && (
              <FlexBox justifyContent="flex-end">
                <ThemeButton
                  color="BLUE_ICE"
                  size="medium"
                  padding="5px 10px !important"
                  onClick={handleClickExcludeAll}
                  startIcon={<Icon name="exclude" size="medium" color="blue-100" />}
                >
                  Exclude all
                </ThemeButton>
              </FlexBox>
            )}
          </Grid>
        </Grid>
      </Box>

      {datasetValues?.mappedColumns?.length ? (
        datasetValues.mappedColumns.map((_, index) => (
          <Field key={index} component={MappingItem} name={`mappedColumns[${index}]`} />
        ))
      ) : (
        <Box position="relative" minHeight={400}>
          <ErrorState error="Something went wrong processing your sheet. The columns could not be mapped correctly." />
        </Box>
      )}
    </MappingContainer>
  );
};

export default DatasetMappingForm;
